import { create } from 'zustand';
import { dayKey } from '../lib/time';

/**
 * Счётчик салавата живёт отдельно от наборов азкаров, и намеренно.
 *
 * У него нет цели, которую надо «дочитать», нет порядка и нет экрана
 * завершения: салават читают сколько получится и в любой момент.
 * Втискивать это в модель набора значило бы навязать ему чужую механику.
 *
 * Единственное, что тут обязательно, — сохранение. Салават копят
 * в течение дня, и счётчик, обнуляющийся при закрытии Telegram,
 * бесполезен. Поэтому, в отличие от прогресса по наборам, состояние
 * пишется в localStorage.
 */

const STORAGE_KEY = 'sa_salawat_v1';

interface Persisted {
  /** Ключ дня, за который накоплено today. Граница — 04:00, как везде. */
  day: string;
  today: number;
  total: number;
}

interface SalawatState extends Persisted {
  tap: () => number;
  resetToday: () => void;
  resetAll: () => void;
}

function load(): Persisted {
  const empty: Persisted = { day: dayKey(), today: 0, total: 0 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const p = JSON.parse(raw) as Partial<Persisted>;
    if (
      typeof p.day !== 'string' ||
      typeof p.today !== 'number' ||
      typeof p.total !== 'number'
    ) {
      return empty;
    }
    // Наступил новый день — дневной счётчик обнуляется, общий копится дальше
    return p.day === dayKey()
      ? { day: p.day, today: p.today, total: p.total }
      : { day: dayKey(), today: 0, total: p.total };
  } catch {
    // Повреждённые данные или запрет на localStorage — начинаем с нуля.
    // Счётчик обязан работать даже так, пусть и без сохранения.
    return empty;
  }
}

function save(p: Persisted): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* приватный режим или переполнение — счётчик продолжает работать */
  }
}

export const useSalawat = create<SalawatState>((set, get) => ({
  ...load(),

  tap: () => {
    const s = get();
    // День мог смениться прямо во время сессии — проверяем на каждом тапе,
    // иначе счёт после полуночи уедет во вчерашний день
    const today = dayKey();
    const base = s.day === today ? s.today : 0;

    const next: Persisted = {
      day: today,
      today: base + 1,
      total: s.total + 1,
    };
    set(next);
    save(next);
    return next.today;
  },

  resetToday: () => {
    const next: Persisted = { day: dayKey(), today: 0, total: get().total };
    set(next);
    save(next);
  },

  resetAll: () => {
    const next: Persisted = { day: dayKey(), today: 0, total: 0 };
    set(next);
    save(next);
  },
}));
